import * as React from "react";
import {useCallback, useContext, useState} from "react";
import {Box, Button, HStack, IconButton, Tab, TabList, TabPanel, TabPanels, Tabs, Tooltip} from "@chakra-ui/react";
import {AddIcon, ChevronLeftIcon, ChevronRightIcon} from "@chakra-ui/icons";
import {BsSoundwave} from "react-icons/bs";
import {v4 as uuidv4} from "uuid";
import {AppContext, AppContextI} from "../../components/context/ContextWrapper";
import {MapContext, MapContextI} from "../../components/context/MapContextWrapper";
import {AreaZoneTemplate, LatLong, MarkerSpot, SpotType, Zone} from "../../components/types";
import {getSoundLibraryDragPayload, hasSoundLibraryDragType, SoundLibraryDragPayload} from "../../components/soundfiles/SoundFileDrawer";
import Spots from "../spots/Spots";
import AreaZonesList from "../areaDetails/zone/AreaZonesList";
import MaintenanceZone from "../areaDetails/zone/MaintenanceZone";
import {
    getDropTargetExitHandler,
    getDroppedFiles,
    getDroppedMarkerPosition,
    getNextAmbientZoneName,
    getNextSpotName,
    hasDroppedFiles,
    PANEL_MAINTENANCE,
    PANEL_SPOTS,
    PANEL_ZONES
} from "./areaPanelHelpers";

type Props = {
    isSoundDrawerOpen: boolean;
    onToggleSoundDrawer: () => void;
    createSpotFromExistingSound: (sound: SoundLibraryDragPayload, position?: LatLong) => Promise<void> | void;
    createSpotsFromDroppedFiles: (files: File[], basePosition?: LatLong) => Promise<void> | void;
    createAmbientZoneFromExistingSound: (sound: SoundLibraryDragPayload) => void;
    createAmbientZonesFromDroppedFiles: (files: File[]) => void;
};

const SpotListView = ({
    isSoundDrawerOpen,
    onToggleSoundDrawer,
    createSpotFromExistingSound,
    createSpotsFromDroppedFiles,
    createAmbientZoneFromExistingSound,
    createAmbientZonesFromDroppedFiles
}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const mapContext: MapContextI = useContext<MapContextI>(MapContext);
    const area = appContext.getArea();
    const defaultRunOnes = !appContext.isOnDemand();
    const activePanel = mapContext.getActivePanel();
    const [isSpotDropTarget, setIsSpotDropTarget] = useState<boolean>(false);
    const [isAmbientDropTarget, setIsAmbientDropTarget] = useState<boolean>(false);

    const handlePanelChange = (nextPanel?: string) => {
        if (!nextPanel || nextPanel === activePanel) {
            return;
        }
        mapContext.setActiveDrawingMode(undefined);
        mapContext.setDrawingModes([]);
        mapContext.setTriggerSelection(undefined);
        mapContext.setSelectedShapeType(undefined);
        appContext.setSelection(undefined);
        mapContext.setActivePanel(nextPanel);
    };

    const activePanelTabIndex = activePanel === PANEL_ZONES
        ? 1
        : activePanel === PANEL_MAINTENANCE
            ? 2
            : 0;

    const createBlankSpot = useCallback(async () => {
        if (!appContext.isDraft()) {
            return;
        }
        const areaId = appContext.getArea()?.id;
        if (!areaId) {
            return;
        }
        const currentSpots = appContext.getSpots() || [];
        const newSpot: MarkerSpot = {
            id: "",
            area: areaId,
            name: getNextSpotName(currentSpots),
            type: SpotType.Marker,
            priority: 2,
            beaconSensitivity: 8,
            pauseOtherAmbients: false,
            duckOtherAmbients: true,
            duckOtherAmbientsDb: 0,
            followMainSound: false,
            playMode: "play",
            playFullAfter: 0,
            loop: false,
            runOnes: defaultRunOnes,
            modifiedAt: 0,
            key: "",
            position: getDroppedMarkerPosition(currentSpots, mapContext.getMapCenter())
        };
        await appContext.createSpot(newSpot);
    }, [appContext, defaultRunOnes, mapContext]);

    const createBlankAmbientZone = useCallback(() => {
        if (!appContext.isDraft()) {
            return;
        }
        const latestArea = appContext.getArea() || area;
        if (!latestArea) {
            return;
        }
        const ambientZones = Array.isArray(latestArea.zones?.ambientZones) ? latestArea.zones.ambientZones : [];
        const nextName = getNextAmbientZoneName(ambientZones);
        const newZone: Zone = {
            ...AreaZoneTemplate,
            id: uuidv4(),
            name: nextName,
            label: nextName,
            soundFiles: {
                ...AreaZoneTemplate.soundFiles
            }
        };
        const nextArea = {
            ...latestArea,
            zones: {
                ...latestArea.zones,
                ambientZones: [...ambientZones, newZone]
            }
        };
        appContext.updateArea(latestArea, nextArea);
    }, [appContext, area]);

    const handleSpotTabDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (!hasDroppedFiles(event.dataTransfer) && !hasSoundLibraryDragType(event.dataTransfer)) {
            setIsSpotDropTarget(false);
            return;
        }
        event.preventDefault();
        const canDrop = appContext.isDraft();
        event.dataTransfer.dropEffect = canDrop ? "copy" : "none";
        setIsSpotDropTarget(canDrop);
    }, [appContext]);

    const handleAmbientTabDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (!hasDroppedFiles(event.dataTransfer) && !hasSoundLibraryDragType(event.dataTransfer)) {
            setIsAmbientDropTarget(false);
            return;
        }
        event.preventDefault();
        const canDrop = appContext.isDraft();
        event.dataTransfer.dropEffect = canDrop ? "copy" : "none";
        setIsAmbientDropTarget(canDrop);
    }, [appContext]);

    const handleSpotTabDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsSpotDropTarget(false);
        const existingSound = getSoundLibraryDragPayload(event.dataTransfer);
        if (existingSound) {
            await createSpotFromExistingSound(existingSound);
            return;
        }
        const files = getDroppedFiles(event.dataTransfer);
        await createSpotsFromDroppedFiles(files);
    }, [createSpotFromExistingSound, createSpotsFromDroppedFiles]);

    const handleAmbientTabDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsAmbientDropTarget(false);
        const existingSound = getSoundLibraryDragPayload(event.dataTransfer);
        if (existingSound) {
            createAmbientZoneFromExistingSound(existingSound);
            return;
        }
        const files = getDroppedFiles(event.dataTransfer);
        createAmbientZonesFromDroppedFiles(files);
    }, [createAmbientZoneFromExistingSound, createAmbientZonesFromDroppedFiles]);

    return (
        <Tabs
            index={activePanelTabIndex}
            onChange={(index) => handlePanelChange(index === 0 ? PANEL_SPOTS : index === 1 ? PANEL_ZONES : PANEL_MAINTENANCE)}
            height={"100%"}
            display={"flex"}
            flexDirection={"column"}
            variant={"enclosed"}
            colorScheme={"blue"}
            isLazy
        >
            <TabList alignItems={"center"}>
                <Tab fontWeight={"bold"}>Spots</Tab>
                <Tab fontWeight={"bold"}>Ambient</Tab>
                <Tab fontWeight={"bold"}>Maintenance</Tab>
                <Box flex={1}/>
                {appContext.isDraft() && activePanel !== PANEL_MAINTENANCE ? (
                    <Tooltip label={activePanel === "zones" ? "Add ambient zone" : "Add spot"}>
                        <IconButton
                            aria-label={activePanel === "zones" ? "Add ambient zone" : "Add spot"}
                            icon={<AddIcon/>}
                            size={"sm"}
                            variant={"outline"}
                            borderColor={"gray.300"}
                            bg={"white"}
                            _hover={{bg: "gray.50", borderColor: "gray.400"}}
                            marginRight={1}
                            onClick={activePanel === "zones" ? createBlankAmbientZone : createBlankSpot}
                        />
                    </Tooltip>
                ) : null}
                <Tooltip label={isSoundDrawerOpen ? "Hide sound files" : "Show sound files"}>
                    <Button
                        aria-label={isSoundDrawerOpen ? "Hide sound file panel" : "Show sound file panel"}
                        onClick={onToggleSoundDrawer}
                        size={"sm"}
                        variant={"outline"}
                        borderColor={"gray.300"}
                        bg={"white"}
                        _hover={{bg: "gray.50", borderColor: "gray.400"}}
                        marginRight={1}
                        minWidth={"auto"}
                        paddingX={1}
                    >
                        <HStack spacing={0.5}>
                            {isSoundDrawerOpen ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                            <BsSoundwave/>
                        </HStack>
                    </Button>
                </Tooltip>
            </TabList>
            <TabPanels flex={1} minHeight={0}>
                <TabPanel px={0} py={4} height={"100%"}>
                    <Box
                        height={"100%"}
                        minHeight={0}
                        borderRadius={"md"}
                        borderWidth={isSpotDropTarget ? "2px" : "0px"}
                        borderStyle={"dashed"}
                        borderColor={isSpotDropTarget ? "blue.300" : "transparent"}
                        backgroundColor={isSpotDropTarget ? "blue.50" : "transparent"}
                        transition={"background-color 120ms ease, border-color 120ms ease"}
                        onDragOver={handleSpotTabDragOver}
                        onDragLeave={getDropTargetExitHandler(setIsSpotDropTarget)}
                        onDrop={handleSpotTabDrop}
                    >
                        <Spots active={activePanel === "spots"}/>
                    </Box>
                </TabPanel>
                <TabPanel px={0} py={4} height={"100%"}>
                    <Box
                        height={"100%"}
                        minHeight={0}
                        borderRadius={"md"}
                        borderWidth={isAmbientDropTarget ? "2px" : "0px"}
                        borderStyle={"dashed"}
                        borderColor={isAmbientDropTarget ? "blue.300" : "transparent"}
                        backgroundColor={isAmbientDropTarget ? "blue.50" : "transparent"}
                        transition={"background-color 120ms ease, border-color 120ms ease"}
                        onDragOver={handleAmbientTabDragOver}
                        onDragLeave={getDropTargetExitHandler(setIsAmbientDropTarget)}
                        onDrop={handleAmbientTabDrop}
                    >
                        <AreaZonesList active={activePanel === "zones"}/>
                    </Box>
                </TabPanel>
                <TabPanel px={0} py={4} height={"100%"}>
                    <MaintenanceZone active={activePanel === "maintenance"} zone={area?.zones?.maintenance}/>
                </TabPanel>
            </TabPanels>
        </Tabs>
    );
};

export default SpotListView;
